import { FieldErrors, useForm, useFormContext, UseFormOptions } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { get } from 'lodash'
import { IShipmentStates, Maybe } from 'types/graphqlTypes'
import * as yup from 'yup'

export interface IRuleNode {
  shipmentInformationOperator?: string
  shipmentInformationFields?: string[]
  shipmentStatusOperator?: string
  shipmentStatusFields?: IShipmentStates[]
  timeWindowField?: string
  timeWindowOperator?: string
  timeWindowTimeAmount?: number
  timeWindowUnit?: string
  shipmentEventField?: string
  shipmentEventOperator?: string
  isShipmentStatus?: boolean
  isTimeWindow?: boolean
  isShipmentInformation?: boolean
  isShipmentEvent?: boolean
}

export interface IIssueRuleForm {
  nodes?: Maybe<IRuleNode[]>
  id?: Maybe<string>
  ruleType: string
}

const INVALID_AMOUNT_MESSAGE = 'Must be in 0.25 hour increments. Max Hours 72'

const NodesSchema = yup.object({
  shipmentInformationOperator: yup.string().when('isShipmentInformation', {
    is: true,
    then: yup.string().required('You must select an option'),
    otherwise: yup.string().notRequired(),
  }),
  shipmentInformationFields: yup.array<string>().when('isShipmentInformation', {
    is: true,
    then: yup.array<string>().required('You must select a field'),
    otherwise: yup.array<string>().notRequired(),
  }),
  shipmentStatusOperator: yup.string().when('isShipmentStatus', {
    is: true,
    then: yup.string().required('You must select an option'),
    otherwise: yup.string().notRequired(),
  }),
  shipmentStatusFields: yup.array<IShipmentStates>().when('isShipmentStatus', {
    is: true,
    then: yup.array<IShipmentStates>().required('You must select a shipment status'),
    otherwise: yup.array<IShipmentStates>().notRequired(),
  }),
  timeWindowField: yup.string().when('isTimeWindow', {
    is: true,
    then: yup.string().required('You must select a field'),
    otherwise: yup.string().notRequired(),
  }),
  timeWindowOperator: yup.string().when('isTimeWindow', {
    is: true,
    then: yup.string().required('You must select an option'),
    otherwise: yup.string().notRequired(),
  }),
  timeWindowTimeAmount: yup.number().when(['isTimeWindow', 'timeWindowUnit'], {
    is: (isTimeWindow, timeWindowUnit) => isTimeWindow && timeWindowUnit === 'hours',
    then: yup
      .number()
      .transform((value, originalValue) => {
        return originalValue === '' ? undefined : value
      })
      .typeError(INVALID_AMOUNT_MESSAGE)
      .required(INVALID_AMOUNT_MESSAGE)
      .min(1, INVALID_AMOUNT_MESSAGE)
      .max(72, INVALID_AMOUNT_MESSAGE)
      .test('validIncrements', INVALID_AMOUNT_MESSAGE, number => number % 0.25 === 0),
    otherwise: yup.number().when(['isTimeWindow', 'timeWindowUnit'], {
      is: (isTimeWindow, timeWindowUnit) => isTimeWindow && timeWindowUnit !== 'hours',
      then: yup.number().required('You must select an amount'),
      otherwise: yup.number().notRequired(),
    }),
  }),
  timeWindowUnit: yup.string().when('isTimeWindow', {
    is: true,
    then: yup.string().required('You must select a unit'),
    otherwise: yup.string().notRequired(),
  }),
  shipmentEventOperator: yup.string().when('isShipmentEvent', {
    is: true,
    then: yup.string().required('You must select an option'),
    otherwise: yup.string().notRequired(),
  }),
  shipmentEventField: yup.string().when('isShipmentEvent', {
    is: true,
    then: yup.string().required('You must select a field'),
    otherwise: yup.string().notRequired(),
  }),
  isShipmentStatus: yup.boolean().default(false),
  isTimeWindow: yup.boolean().default(false),
  isShipmentInformation: yup.boolean().default(false),
  isShipmentEvent: yup.boolean().default(false),
})

export const issueRuleFormSchemaObject = {
  nodes: yup.array().of(NodesSchema),
  ruleType: yup.string().required('You must select a rule type'),
  id: yup.string().notRequired().nullable(),
}

const issueRuleFormSchema = yup.object<IIssueRuleForm>().shape(issueRuleFormSchemaObject)

export type TIssueRuleFormFormSchema = yup.InferType<typeof issueRuleFormSchema>

interface IUseIssueRuleFormProps
  extends Omit<UseFormOptions<TIssueRuleFormFormSchema>, 'resolver'> {}

export const useIssueRuleForm = ({ defaultValues = {}, ...props }: IUseIssueRuleFormProps) =>
  useForm<TIssueRuleFormFormSchema>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(issueRuleFormSchema),
    defaultValues,
    ...props,
  })

export const useIssueRuleFormContext = () => useFormContext<TIssueRuleFormFormSchema>()

export const getErrorMessage = <T extends FieldErrors<TIssueRuleFormFormSchema>>(
  errors: T,
  keyNames: Parameters<typeof get>[1]
): string | undefined => {
  const { message } = get(errors, keyNames, { message: undefined })
  return message
}
